import { DataSheet, PaginatedDataSheetLogisticsEstimates } from '@lune-fe/dapi-models'
import { AxiosError } from 'axios'
import fileDownload from 'js-file-download'

import { downloadDataSheet } from 'endpoints/dapi'

export type DataSheetType = DataSheet['type']
export type DataSheetTypeOrUnknown = NonNullable<DataSheetType> | 'unknown'

// Extract the location type from logistics estimates
type LogisticsLocation = PaginatedDataSheetLogisticsEstimates['data'][0]['source']

/**
 * Formats complex location objects into readable strings
 * Handles address, lat/lon, LOCODE, and airport code formats
 */
export const formatLocation = (location: LogisticsLocation): string => {
    if (!location) return '--'

    // Address format
    if ('countryCode' in location) {
        const parts = [location.city, location.postcode, location.countryCode].filter(Boolean)
        return parts.join(', ')
    }

    // Lat/Lon format
    if ('lat' in location && 'lon' in location) {
        return `${location.lat.toFixed(4)}, ${location.lon.toFixed(4)}`
    }

    // LOCODE format
    if ('locode' in location) {
        return location.locode
    }

    // Airport code format
    if ('code' in location) {
        return location.code
    }

    return '--'
}

export const dataSheetTypeConversion: Record<DataSheetTypeOrUnknown, string> = {
    lune: 'Lune',
    ecotransit: 'EcoTransIT CSV',
    lune_logistics_v2: 'Lune v2 CSV template',
    lune_fintech: 'Spend CSV',
    rfq: 'Tender',
    logistics_user_template: 'Lune template',
    estimate_sheet_v1: 'Estimate sheet',
    shipment_sheet_v1: 'Shipment sheet',
    unknown: '-',
}

export const getDownloadType = (
    type: DataSheetTypeOrUnknown,
): 'fintech' | 'logistics' | 'rfq' | 'logistics_user_template' => {
    switch (type) {
        case 'lune':
        case 'lune_logistics_v2':
        case 'ecotransit':
            return 'logistics'
        case 'rfq':
            return 'rfq'
        case 'lune_fintech':
            return 'fintech'
        case 'logistics_user_template':
            return 'logistics_user_template'
        default:
            return 'logistics'
    }
}

export const downloadSheet = async (
    id: string,
    type: 'fintech' | 'logistics' | 'rfq' | 'logistics_user_template',
) => {
    try {
        if (type === 'rfq') {
            const rawData = await downloadDataSheet(id)
            // Due to how file-download works, we need to convert to a Buffer/Blob here
            // otherwise the resulting file will be corrupted. This has been tested against
            // real S3 data and it works correctly.
            //
            // See: https://github.com/kennethjiang/js-file-download/blob/0c20260f6f3dbbc94082f9f204e32322e7323294/README.md#binary-downloads
            const buffer = Buffer.from(rawData)
            fileDownload(
                buffer,
                `${id}.xlsx`,
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            )
        } else {
            const rawData = await downloadDataSheet(id)
            const data = new TextDecoder().decode(rawData)
            fileDownload(data, `${id}.csv`, 'text/csv')
        }
    } catch (err) {
        if (err instanceof AxiosError && err.status === 404) {
            // swallow, users can change the page url to trigger this error
            return
        }
        throw err
    }
}
